import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaPlus, FaImage, FaRegClock, FaChevronDown, FaChevronUp, FaTrash } from 'react-icons/fa';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import api from '../../../services/api';

// Détecte http(s)://, mailto:, tel:, www.* et les rend cliquables.
const URL_REGEX = /((?:https?:\/\/|mailto:|tel:)[^\s<>()]+|www\.[^\s<>()]+)/gi;
const trimTrailingPunct = (s) => s.replace(/[.,)!?;:]+$/, '');
const linkify = (text) => {
  if (!text) return null;
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) => {
    if (URL_REGEX.test(part)) {
      URL_REGEX.lastIndex = 0;
      const trailing = part.match(/[.,)!?;:]+$/)?.[0] || '';
      const display = trailing ? part.slice(0, -trailing.length) : part;
      const href = /^www\./i.test(display) ? `https://${trimTrailingPunct(display)}` : trimTrailingPunct(display);
      return (
        <React.Fragment key={i}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-fideliz-blue underline hover:opacity-80 break-words"
            onClick={(e) => e.stopPropagation()}
          >
            {display}
          </a>
          {trailing}
        </React.Fragment>
      );
    }
    URL_REGEX.lastIndex = 0;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const PublishPage = () => {
  const { brand, tenantHeaders, brandName } = useProContext();

  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null);

  // Form
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      const { data } = await api.get('/feed', { headers: tenantHeaders(), params: { limit: 50 } });
      setPosts(Array.isArray(data) ? data : data.data || []);
    } catch { setPosts([]); }
    setPostsLoading(false);
  }, [tenantHeaders]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const [uploadError, setUploadError] = useState('');
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/upload', formData, {
        headers: { ...tenantHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setImageUrl(data.file_url);
    } catch (err) {
      console.error(err);
      setUploadError(err?.response?.data?.message || 'Échec de l\'upload');
    }
    setUploading(false);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer définitivement cette publication ?')) return;
    setDeletingId(postId);
    try {
      await api.delete(`/feed/${postId}`, { headers: tenantHeaders() });
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (e) {
      console.error(e);
      window.alert(e?.response?.data?.message || 'Échec de la suppression');
    }
    setDeletingId(null);
  };

  const handlePublish = async () => {
    if (!body.trim()) return;
    setPublishing(true);
    try {
      await api.post('/feed', { title: title.trim() || undefined, body: body.trim(), image_url: imageUrl || undefined }, { headers: tenantHeaders() });
      setTitle(''); setBody(''); setImageUrl(''); setShowForm(false);
      await fetchPosts();
    } catch (e) { console.error(e); }
    setPublishing(false);
  };

  const formatDate = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diffH = Math.floor((now - date) / 3600000);
    if (diffH < 1) return "A l'instant";
    if (diffH < 24) return `Il y a ${diffH}h`;
    if (diffH < 48) return 'Hier';
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  };

  const groupByEmoji = (details) => {
    if (!details || details.length === 0) return {};
    return details.reduce((acc, r) => {
      if (!acc[r.emoji]) acc[r.emoji] = [];
      acc[r.emoji].push(r.userName);
      return acc;
    }, {});
  };

  return (
    <ProLayout title="Mes actus">
      <div className="max-w-3xl mx-auto p-6 space-y-6">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Publications</h2>
            <p className="text-sm text-gray-400 mt-0.5">{posts.length} article{posts.length > 1 ? 's' : ''} publié{posts.length > 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl transition-all hover:opacity-90"
            style={{ backgroundColor: brand }}
          >
            {showForm ? <FaTimes className="w-3.5 h-3.5" /> : <FaPlus className="w-3.5 h-3.5" />}
            {showForm ? 'Annuler' : 'Nouvel article'}
          </button>
        </div>

        {/* ── Compose form ── */}
        {showForm && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {/* Image zone */}
            {imageUrl ? (
              <div className="relative">
                <img src={imageUrl} alt="" className="w-full h-56 object-cover" />
                <button onClick={() => setImageUrl('')} className="absolute top-3 right-3 w-8 h-8 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80 transition-colors">
                  <FaTimes className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <>
                <label className={`flex flex-col items-center justify-center h-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors border-b border-gray-100 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <FaImage className="w-6 h-6 text-gray-300 mb-2" />
                  <span className="text-sm text-gray-400">{uploading ? 'Envoi en cours...' : 'Cliquez pour ajouter une image'}</span>
                  <span className="text-[0.6875rem] text-gray-300 mt-1">Recommandé : 1200x630px</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
                {uploadError && (
                  <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100">{uploadError}</div>
                )}
              </>
            )}

            {/* Text fields */}
            <div className="p-5 space-y-4">
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Titre de l'article"
                className="w-full text-lg font-semibold text-gray-900 placeholder-gray-300 focus:outline-none border-none bg-transparent"
              />
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Écrivez votre article ici..."
                rows={5}
                className="w-full text-sm text-gray-700 placeholder-gray-300 focus:outline-none border-none bg-transparent resize-none leading-relaxed"
              />
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <p className="text-[0.6875rem] text-gray-300">{body.length} caractères</p>
                <button
                  onClick={handlePublish}
                  disabled={!body.trim() || publishing}
                  className="px-6 py-2 text-white text-sm font-semibold rounded-xl disabled:opacity-40 transition-all hover:opacity-90"
                  style={{ backgroundColor: brand }}
                >
                  {publishing ? 'Publication...' : 'Publier'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Posts feed ── */}
        {postsLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="h-48 bg-gray-100 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-gray-100 rounded w-2/3 animate-pulse" />
                  <div className="h-4 bg-gray-50 rounded w-full animate-pulse" />
                  <div className="h-4 bg-gray-50 rounded w-3/4 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 && !showForm ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: brand + '10' }}>
              <FaImage className="w-7 h-7" style={{ color: brand }} />
            </div>
            <p className="text-base font-semibold text-gray-900 mb-1">Aucune publication</p>
            <p className="text-sm text-gray-400 mb-6">Partagez des actus avec vos clients</p>
            <button onClick={() => setShowForm(true)} className="px-5 py-2.5 text-white text-sm font-semibold rounded-xl" style={{ backgroundColor: brand }}>
              Ecrire un premier article
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map(post => {
              const counts = post.reaction_counts || {};
              const total = Object.values(counts).reduce((a, b) => a + b, 0);
              const details = post.reaction_details || [];
              const grouped = groupByEmoji(details);
              const isExpanded = expandedPost === post.id;

              return (
                <article key={post.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors">
                  {/* Image */}
                  {post.image_url && (
                    <img src={post.image_url} alt="" className="w-full h-56 object-cover" />
                  )}

                  <div className="p-5">
                    {/* Meta */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[0.625rem] font-bold" style={{ backgroundColor: brand }}>
                        {(brandName || 'P')[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[0.75rem] font-semibold text-gray-900">{brandName}</p>
                        <div className="flex items-center gap-1 text-[0.6875rem] text-gray-400">
                          <FaRegClock className="w-2.5 h-2.5" />
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(post.id)}
                        disabled={deletingId === post.id}
                        title="Supprimer cette publication"
                        className="p-2 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </div>

                    {/* Title */}
                    {post.title && (
                      <h3 className="text-base font-bold text-gray-900 mb-2">{post.title}</h3>
                    )}

                    {/* Body */}
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{linkify(post.body)}</p>

                    {/* Reactions bar */}
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      {total > 0 ? (
                        <div>
                          {/* Summary row — clickable */}
                          <button
                            onClick={() => setExpandedPost(isExpanded ? null : post.id)}
                            className="flex items-center justify-between w-full group"
                          >
                            <div className="flex items-center gap-1">
                              {Object.entries(counts).filter(([, v]) => v > 0).map(([emoji]) => (
                                <span key={emoji} className="text-sm">{emoji}</span>
                              ))}
                              <span className="text-[0.75rem] text-gray-500 ml-1.5">{total} reaction{total > 1 ? 's' : ''}</span>
                            </div>
                            {details.length > 0 && (
                              isExpanded
                                ? <FaChevronUp className="w-3 h-3 text-gray-300 group-hover:text-gray-500" />
                                : <FaChevronDown className="w-3 h-3 text-gray-300 group-hover:text-gray-500" />
                            )}
                          </button>

                          {/* Expanded: who reacted */}
                          {isExpanded && details.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {Object.entries(grouped).map(([emoji, names]) => (
                                <div key={emoji} className="flex items-start gap-2">
                                  <span className="text-sm mt-0.5">{emoji}</span>
                                  <div className="flex flex-wrap gap-1.5">
                                    {names.map((name, i) => (
                                      <span key={i} className="text-[0.6875rem] bg-gray-50 text-gray-600 px-2 py-0.5 rounded-full">{name}</span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-[0.75rem] text-gray-300">Aucune reaction pour le moment</p>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </ProLayout>
  );
};

export default PublishPage;
